import { useEffect, useState, type CSSProperties, type RefObject } from 'react';
import { Vec3 } from '@mindcloudxai_3d/core';
import type { RendererCameraReader, RendererProjector } from '@renderer/index';
import type { Hotspot } from '@app-types/scene';
import { useViewerStore } from '@stores/viewerStore';
import { getHotspotAnchor } from './data';

interface HotspotLayerProps {
  containerRef: RefObject<HTMLDivElement | null>;
  getController: () => (RendererCameraReader & RendererProjector) | null;
}

interface MarkerState {
  id: string;
  index: number;
  x: number;
  y: number;
  distance: number;
  visible: boolean;
}

const EMPTY_MARKERS: MarkerState[] = [];
const EMPTY_HOTSPOTS: Hotspot[] = [];

export function HotspotLayer({ containerRef, getController }: HotspotLayerProps) {
  const sceneConfig = useViewerStore((s) => s.sceneConfig);
  const activeIndex = useViewerStore((s) => s.activeHotspotIndex);
  const setActiveIndex = useViewerStore((s) => s.setActiveHotspotIndex);
  const [markers, setMarkers] = useState<MarkerState[]>([]);

  const hotspots = sceneConfig?.hotspots ?? EMPTY_HOTSPOTS;

  useEffect(() => {
    if (hotspots.length === 0) {
      return;
    }

    let rafId = 0;

    const update = () => {
      const controller = getController();
      const container = containerRef.current;

      if (!controller || !container) {
        setMarkers(EMPTY_MARKERS);
        rafId = requestAnimationFrame(update);
        return;
      }

      const rect = container.getBoundingClientRect();
      const camera = controller.camera.get();
      setMarkers(
        hotspots.map((hotspot, index) => {
          // 投影从 viewer 获取；距离仅用于 UI 展开态，由相机位置自算
          const anchor = getHotspotAnchor(hotspot);
          const projected = controller.camera.project(anchor);
          return {
            id: hotspot.id,
            index,
            x: (projected?.clientX ?? 0) - rect.left,
            y: (projected?.clientY ?? 0) - rect.top,
            distance: Vec3.from(camera.position).distanceTo(anchor),
            visible: projected?.visible ?? false,
          };
        }),
      );
      rafId = requestAnimationFrame(update);
    };

    rafId = requestAnimationFrame(update);
    return () => cancelAnimationFrame(rafId);
  }, [containerRef, getController, hotspots]);

  if (hotspots.length === 0 || markers.length === 0) return null;

  return (
    <div className="pointer-events-none absolute inset-0 z-[8] overflow-hidden">
      {markers.map((marker) => {
        const hotspot = hotspots[marker.index];
        const isActive = marker.index === activeIndex;
        const expanded = isActive || marker.distance < 4.5;
        const style: CSSProperties = {
          transform: `translate3d(${marker.x}px, ${marker.y}px, 0) translate(-50%, -50%)`,
          opacity: marker.visible ? 1 : 0,
          pointerEvents: marker.visible ? 'auto' : 'none',
          zIndex: isActive ? 30 : 20,
        };

        return (
          <button
            key={marker.id}
            type="button"
            aria-label={`Open hotspot ${hotspot.title}`}
            onClick={() => setActiveIndex(marker.index)}
            className="group pointer-events-auto absolute flex items-center gap-2 rounded-full transition-opacity duration-200 outline-none"
            style={style}
          >
            <span
              className={`grid h-6 w-6 place-items-center rounded-full border border-white/80 shadow-[0_0_24px_rgba(35,95,238,0.65)] transition-transform group-hover:scale-110 ${
                isActive ? 'bg-[#235FEE]' : 'bg-white/85'
              }`}
            >
              <span
                className={`h-2.5 w-2.5 rounded-full ${isActive ? 'bg-white' : 'bg-[#235FEE]'}`}
              />
            </span>
            <span
              className={`max-w-44 truncate rounded-full bg-black/60 px-3 py-1 text-xs font-medium text-white shadow-lg backdrop-blur-md transition-all duration-200 group-hover:max-w-44 group-hover:opacity-100 ${
                expanded ? 'max-w-44 opacity-100' : 'max-w-0 px-0 opacity-0'
              }`}
            >
              {hotspot.title}
            </span>
          </button>
        );
      })}
    </div>
  );
}
